import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../client';
import type {
  LocationUpdatePayload,
  LocationUpdateResponse,
  LocationTogglePayload,
  LocationToggleResponse,
  LocationStatus,
  WorkerLocationsResponse,
  NearbyWorkersResponse,
} from '../types/location';

/** Query keys */
export const locationKeys = {
  all: ['location'] as const,
  status: () => [...locationKeys.all, 'status'] as const,
  workers: () => [...locationKeys.all, 'workers'] as const,
  nearbyWorkers: (incidentId: string) => [...locationKeys.all, 'nearby', incidentId] as const,
};

/** Get current user's location status */
export function useLocationStatus() {
  return useQuery({
    queryKey: locationKeys.status(),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<LocationStatus>('/api/v1/location/status/');
      return response.data;
    },
  });
}

/** Update current user's location */
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: LocationUpdatePayload) => {
      const client = getApiClient();
      const response = await client.post<LocationUpdateResponse>(
        '/api/v1/location/update/',
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.status() });
      queryClient.invalidateQueries({ queryKey: locationKeys.workers() });
    },
  });
}

/** Toggle location sharing on/off */
export function useToggleLocationSharing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: LocationTogglePayload) => {
      const client = getApiClient();
      const response = await client.post<LocationToggleResponse>(
        '/api/v1/location/toggle/',
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.status() });
      queryClient.invalidateQueries({ queryKey: locationKeys.workers() });
    },
  });
}

/** Get all worker locations for map display */
export function useWorkerLocations() {
  return useQuery({
    queryKey: locationKeys.workers(),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<WorkerLocationsResponse>('/api/v1/location/workers/');
      return response.data;
    },
    // Refresh every 30 seconds for live updates
    refetchInterval: 30000,
  });
}

/** Get nearby workers for an incident */
export function useNearbyWorkers(incidentId: string, options?: { limit?: number; enabled?: boolean }) {
  return useQuery({
    queryKey: locationKeys.nearbyWorkers(incidentId),
    queryFn: async () => {
      const client = getApiClient();
      const response = await client.get<NearbyWorkersResponse>(
        `/api/v1/incidents/${incidentId}/nearby-workers/`,
        { params: { limit: options?.limit } }
      );
      return response.data;
    },
    enabled: options?.enabled !== false && !!incidentId,
  });
}
